import { useState, useMemo, useCallback, useRef } from 'react'
import { useSearchParams as useRouterSearchParams } from 'react-router-dom'
import { format, subDays } from 'date-fns'
import {
  Search,
  ChevronDown,
  ChevronRight,
  FileText,
  FileSpreadsheet,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import {
  fetchScenarioRuns,
  fetchErrorEvents,
  useForklifts,
  useMultiForkliftStats,
  useMultiScenarioStats,
} from '@/hooks/useForklifts'
import LoadingSpinner from '@/components/common/LoadingSpinner'
import PreviewPathCanvas from '@/components/maps/PreviewPathCanvas'
import { getScenarioLabel } from '@/common/scenarioLabel'
import { formatDuration } from '@/common/duration'
import { buildMultiScenarioRunsCsv } from '@/common/scenarioRunsCsv'
import { buildMultiErrorEventsCsv } from '@/common/errorEventsCsv'
import { joinForkIds, splitForkIds } from '@/common/mergeStats'
import { SCENARIO_COLORS } from '@/common/scenarioColors'
import { exportElementToA4Pdf } from '@/common/metricsReportPdf'
import ExportMenu from '@/components/common/ExportMenu'
import MetricsReport from '@/components/analytics/MetricsReport'
import ForkliftMultiSelect from '@/components/analytics/ForkliftMultiSelect'
import ScenarioRunTimeline, {
  type TimelineOrientation,
} from '@/components/analytics/ScenarioRunTimeline'
import { buildForkLanes } from '@/common/forkLanes'

const PERIOD_OPTIONS = [
  { value: '7d', label: '過去7日間' },
  { value: '30d', label: '過去30日間' },
  { value: '90d', label: '過去90日間' },
  { value: '365d', label: '過去1年間' },
  { value: 'all', label: '全期間' },
  { value: 'custom', label: 'カスタム' },
]

// `'all'` 選択時に開始日として使うフロアー値（運用開始月以前の遠い過去日）
const ALL_TIME_START = '2026-01-01'

function getPeriodDates(period: string): { start: string; end: string } | null {
  const today = format(new Date(), 'yyyy-MM-dd')
  if (period === 'all') {
    return { start: ALL_TIME_START, end: today }
  }
  const match = period.match(/^(\d+)d$/)
  if (match) {
    const days = parseInt(match[1], 10)
    const raw = format(subDays(new Date(), days), 'yyyy-MM-dd')
    // ALL_TIME_START より前になる場合は ALL_TIME_START にクランプ
    const start = raw < ALL_TIME_START ? ALL_TIME_START : raw
    return { start, end: today }
  }
  return null
}

export default function MetricsPage() {
  const [urlParams, setUrlParams] = useRouterSearchParams()

  // fork_id はカンマ区切りで複数指定可 (他画面からの単体リンクとも互換)
  const initialForkIds = splitForkIds(urlParams.get('fork_id'))
  const urlStart = urlParams.get('start_date')
  const urlEnd = urlParams.get('end_date')
  // URL に明示的な日付があればカスタム扱い、なければ「過去7日間」をデフォルトに
  const initialPeriod: string = urlStart || urlEnd ? 'custom' : '7d'
  const defaultPeriodDates =
    getPeriodDates(initialPeriod) ?? getPeriodDates('7d')!
  const initialStart = urlStart ?? defaultPeriodDates.start
  const initialEnd = urlEnd ?? defaultPeriodDates.end

  const [selectedForkIds, setSelectedForkIds] =
    useState<string[]>(initialForkIds)
  const [period, setPeriod] = useState<string>(initialPeriod)
  const [dateRange, setDateRange] = useState({
    start: initialStart,
    end: initialEnd,
  })
  const [showSimOnly, setShowSimOnly] = useState(false)

  // URLにfork_idが指定されている場合は初期検索を実行
  const [searchParams, setSearchParams] = useState<{
    forkIds: string[]
    start_date: string
    end_date: string
  } | null>(
    initialForkIds.length > 0
      ? {
          forkIds: initialForkIds,
          start_date: initialStart,
          end_date: initialEnd,
        }
      : null
  )

  const applySearch = (forkIds: string[], start: string, end: string) => {
    const params = { forkIds, start_date: start, end_date: end }
    setSearchParams(params)
    setUrlParams(
      {
        fork_id: joinForkIds(params.forkIds),
        start_date: params.start_date,
        end_date: params.end_date,
      },
      { replace: true }
    )
  }

  const handleSearch = () => {
    if (selectedForkIds.length === 0) return
    applySearch(selectedForkIds, dateRange.start, dateRange.end)
  }

  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod)
    const dates = getPeriodDates(newPeriod)
    if (!dates) return
    setDateRange(dates)
    // 検索済みなら新しい期間で即時再取得
    if (searchParams && searchParams.forkIds.length > 0) {
      applySearch(searchParams.forkIds, dates.start, dates.end)
    }
  }

  const {
    data: forkliftsData,
    isLoading: loadingForklifts,
    isFetching: fetchingForklifts,
  } = useForklifts({
    period: 'all',
    show_sim: showSimOnly ? 'true' : 'false',
  })
  const {
    data: stats,
    isLoading: loadingStats,
    isFetching: fetchingStats,
  } = useMultiForkliftStats({
    forkIds: searchParams?.forkIds ?? [],
    start_date: searchParams?.start_date ?? '',
    end_date: searchParams?.end_date ?? '',
  })
  const {
    data: scenarioStats,
    isLoading: loadingScenarios,
    isFetching: fetchingScenarios,
  } = useMultiScenarioStats({
    forkIds: searchParams?.forkIds ?? [],
    start_date: searchParams?.start_date ?? '',
    end_date: searchParams?.end_date ?? '',
  })

  const forklifts = forkliftsData?.data || []
  const isMultiFork = (searchParams?.forkIds.length ?? 0) > 1
  // ファイル名・レポート見出し用の機体ラベル
  const searchedForkIds = searchParams?.forkIds ?? []
  const isAllForks =
    forklifts.length > 0 &&
    searchedForkIds.length === forklifts.length &&
    forklifts.every((f) => searchedForkIds.includes(f.fork_id))
  const forkLabel = isAllForks
    ? `すべて (${searchedForkIds.length}台: ${searchedForkIds.join(', ')})`
    : searchedForkIds.join(', ')
  const forkFileTag = isAllForks ? 'all' : searchedForkIds.join('+')
  const isAnyFetching = fetchingForklifts || fetchingStats || fetchingScenarios
  const isLoading = loadingStats || loadingScenarios

  const [expandedScenarioId, setExpandedScenarioId] = useState<string | null>(
    null
  )

  const toggleExpand = useCallback((id: string) => {
    setExpandedScenarioId((prev) => (prev === id ? null : id))
  }, [])

  // シナリオID一覧を抽出（実行回数の降順 = scenarios の順序）
  const scenarioIds = useMemo(() => {
    if (!scenarioStats?.scenarios) return []
    return scenarioStats.scenarios.map((s) => s.id)
  }, [scenarioStats?.scenarios])

  // ID→表示ラベルのマッピング（グラフ用）
  const scenarioLabelMap = useMemo(() => {
    if (!scenarioStats?.scenarios) return {} as Record<string, string>
    return Object.fromEntries(
      scenarioStats.scenarios.map((s) => [s.id, getScenarioLabel(s)])
    )
  }, [scenarioStats?.scenarios])

  // ID→色のマッピング（日別実行数の積み上げと実行時刻タイムラインで共通）
  const scenarioColorMap = useMemo(
    () =>
      Object.fromEntries(
        scenarioIds.map((id, i) => [
          id,
          SCENARIO_COLORS[i % SCENARIO_COLORS.length],
        ])
      ) as Record<string, string>,
    [scenarioIds]
  )

  // 複数機体の合算時は日別グラフを機体別に積み上げる (シナリオ別は重すぎる)。
  // 色は合算の並び順 (scenario-stats の fork_ids) で割り当て、タイムラインと揃える
  const forkLanes = useMemo(
    () => buildForkLanes(scenarioStats?.fork_ids),
    [scenarioStats?.fork_ids]
  )
  const useForkBreakdown = isMultiFork && forkLanes.length > 0

  // シナリオ名の補完に scenario-stats を使うため、全データの取得完了を待つ。
  // 実行単位の行 (scenario-runs) は旧ブリッジ機体で数十秒かかるため、クリック時に取得する
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false)
  // 日別シナリオ実行時刻グラフの軸 (date-x: 横=日付/縦=時刻、time-x: 横=時刻/縦=日付)。
  // PDF レポートにも同じ軸で出す (time-x は日数ぶん分割して描く)
  const [timelineOrientation, setTimelineOrientation] =
    useState<TimelineOrientation>('date-x')
  const canDownloadCsv =
    !!searchParams && !isAnyFetching && !!scenarioStats && !isDownloadingCsv

  // 実行行に名前が無い (PLC 起動等) 場合は scenario-stats の表示名で補完する
  const handleDownloadCsv = async () => {
    if (!searchParams) return
    setIsDownloadingCsv(true)
    try {
      const scenarioRuns = await Promise.all(
        searchParams.forkIds.map((forkId) =>
          fetchScenarioRuns({
            forkId,
            start_date: searchParams.start_date,
            end_date: searchParams.end_date,
          })
        )
      )
      if (scenarioRuns.every((r) => r.runs.length === 0)) {
        alert('この機体・期間にはシナリオ実行の記録がありません')
        return
      }
      const csv = buildMultiScenarioRunsCsv(scenarioRuns, scenarioLabelMap)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `scenario_runs_${forkFileTag}_${searchParams.start_date}_${searchParams.end_date}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('CSV の取得に失敗しました', err)
      alert('CSV の取得に失敗しました')
    } finally {
      setIsDownloadingCsv(false)
    }
  }

  // エラー一覧 CSV: 機体イベントジャーナルのエラー発生 1 件 = 1 行 (再構成分を含む)
  const [isDownloadingErrorCsv, setIsDownloadingErrorCsv] = useState(false)
  const canDownloadErrorCsv =
    !!searchParams &&
    !isAnyFetching &&
    !!scenarioStats &&
    !isDownloadingErrorCsv
  const handleDownloadErrorCsv = async () => {
    if (!searchParams) return
    setIsDownloadingErrorCsv(true)
    try {
      const list = await Promise.all(
        searchParams.forkIds.map((forkId) =>
          fetchErrorEvents({
            forkId,
            start_date: searchParams.start_date,
            end_date: searchParams.end_date,
          })
        )
      )
      if (list.every((r) => r.events.length === 0)) {
        alert('この機体・期間にはエラー発生の記録がありません')
        return
      }
      const csv = buildMultiErrorEventsCsv(list, scenarioLabelMap)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `error_events_${forkFileTag}_${searchParams.start_date}_${searchParams.end_date}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('エラー一覧 CSV の取得に失敗しました', err)
      alert('エラー一覧 CSV の取得に失敗しました')
    } finally {
      setIsDownloadingErrorCsv(false)
    }
  }

  // PDF レポート: 画面外にレポートレイアウトを描画し、描画完了後にキャプチャする
  const canExportPdf =
    !!searchParams && !isLoading && !isAnyFetching && !!scenarioStats
  const [isExportingPdf, setIsExportingPdf] = useState(false)
  const reportRef = useRef<HTMLDivElement>(null)
  // 選択機体がすべて同じサイトならレポートにサイト名を出す
  const searchedSiteIds = Array.from(
    new Set(
      forklifts
        .filter((f) => searchedForkIds.includes(f.fork_id))
        .map((f) => f.site_id)
    )
  )
  const reportSiteId =
    searchedSiteIds.length === 1 ? searchedSiteIds[0] : undefined

  const handleReportRendered = async () => {
    const element = reportRef.current
    if (!element || !searchParams) {
      setIsExportingPdf(false)
      return
    }
    try {
      await exportElementToA4Pdf(
        element,
        `metrics_report_${forkFileTag}_${searchParams.start_date}_${searchParams.end_date}.pdf`
      )
    } catch (err) {
      console.error('PDF レポートの生成に失敗しました', err)
      alert('PDF レポートの生成に失敗しました')
    } finally {
      setIsExportingPdf(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-900">メトリクス</h1>
        {isAnyFetching && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <LoadingSpinner size="sm" />
            <span>読み込み中...</span>
          </div>
        )}
        <ExportMenu
          className="ml-auto"
          disabled={!searchParams}
          busyLabel={
            isExportingPdf
              ? 'PDF 生成中...'
              : isDownloadingCsv || isDownloadingErrorCsv
                ? 'CSV 取得中...'
                : null
          }
          items={[
            {
              key: 'pdf',
              label: 'レポート（PDF）',
              icon: <FileText className="h-4 w-4 text-gray-500" />,
              disabled: !canExportPdf,
              title: 'この画面の数値とグラフを A4 レイアウトで出力',
              onSelect: () => setIsExportingPdf(true),
            },
            {
              key: 'csv',
              label: 'データ（CSV）',
              icon: <FileSpreadsheet className="h-4 w-4 text-gray-500" />,
              disabled: !canDownloadCsv,
              title:
                'シナリオ実行ごとの行（荷積み/荷下ろし回数・エラー回数・実行時間・走行距離・出荷時からの累積）',
              onSelect: handleDownloadCsv,
            },
            {
              key: 'error-csv',
              label: 'エラー一覧（CSV）',
              icon: <FileSpreadsheet className="h-4 w-4 text-gray-500" />,
              disabled: !canDownloadErrorCsv,
              title:
                'エラー発生ごとの行（発生/回復時刻・コード・エラー名・発生時のシナリオ・エラー停止の要因）',
              onSelect: handleDownloadErrorCsv,
            },
          ]}
        />
      </div>

      {/* フィルター */}
      <div className="flex flex-wrap items-center gap-4 bg-white rounded-lg shadow p-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            フォークリフト
          </label>
          {loadingForklifts ? (
            <div className="flex items-center gap-2 px-4 py-2">
              <LoadingSpinner size="sm" />
              <span className="text-sm text-gray-500">読み込み中...</span>
            </div>
          ) : (
            <ForkliftMultiSelect
              forklifts={forklifts}
              selected={selectedForkIds}
              onChange={setSelectedForkIds}
              disabled={isAnyFetching}
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            &nbsp;
          </label>
          <button
            onClick={() => setShowSimOnly((v) => !v)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              showSimOnly
                ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            {showSimOnly ? 'SIM 表示中' : 'SIM 非表示'}
          </button>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            期間
          </label>
          <select
            value={period}
            onChange={(e) => handlePeriodChange(e.target.value)}
            disabled={isAnyFetching}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            開始日
          </label>
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => {
              setDateRange((r) => ({ ...r, start: e.target.value }))
              setPeriod('custom')
            }}
            disabled={isAnyFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            終了日
          </label>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => {
              setDateRange((r) => ({ ...r, end: e.target.value }))
              setPeriod('custom')
            }}
            disabled={isAnyFetching}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            &nbsp;
          </label>
          <button
            onClick={handleSearch}
            disabled={selectedForkIds.length === 0 || isAnyFetching}
            className="flex items-center gap-2 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Search className="h-4 w-4" />
            検索
          </button>
        </div>
      </div>

      {/* コンテンツ */}
      {!searchParams ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          フォークリフトと期間を選択して検索してください
        </div>
      ) : (
        <>
          {isMultiFork && (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-900">
                {searchedForkIds.length}台
              </span>{' '}
              の合算: {searchedForkIds.join(', ')}
            </p>
          )}

          {/* サマリーカード */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${isAnyFetching ? 'opacity-60' : ''}`}
          >
            {isLoading ? (
              <>
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-lg shadow p-3 animate-pulse"
                  >
                    <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">
                    シナリオ実行回数
                  </p>
                  <p className="text-xl font-bold text-primary-600 mt-1">
                    {scenarioStats?.total_executions ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      回
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">完了回数</p>
                  <p className="text-xl font-bold text-green-600 mt-1">
                    {scenarioStats?.total_completions ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      回
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">搬送回数</p>
                  <p className="text-xl font-bold text-teal-600 mt-1">
                    {scenarioStats?.total_transports ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      回
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">
                    エラー停止回数
                  </p>
                  <p className="text-xl font-bold text-orange-500 mt-1">
                    {scenarioStats?.total_error_stops ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      回
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">
                    総走行距離
                  </p>
                  <p className="text-xl font-bold text-blue-600 mt-1">
                    {stats?.summary.total_distance_km ?? 0}
                    <span className="text-sm font-normal text-gray-400 ml-1">
                      km
                    </span>
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">
                    シナリオ実行時間（期間）
                  </p>
                  <p className="text-lg font-bold text-indigo-600 mt-1">
                    {formatDuration(scenarioStats?.total_duration_ms ?? 0)}
                  </p>
                </div>
                <div className="bg-white rounded-lg shadow p-3">
                  <p className="text-xs font-medium text-gray-500">
                    シナリオ実行時間（いままでの累計）
                  </p>
                  <p className="text-lg font-bold text-indigo-600 mt-1">
                    {formatDuration(scenarioStats?.cumulative_duration_ms ?? 0)}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* 日別グラフ */}
          {!isLoading && (
            <div
              className={`grid grid-cols-1 lg:grid-cols-2 gap-4 ${isAnyFetching ? 'opacity-60' : ''}`}
            >
              {/* 日別シナリオ実行数（積み上げ） */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  日別シナリオ実行数
                </h2>
                {useForkBreakdown &&
                scenarioStats?.fork_daily &&
                scenarioStats.fork_daily.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={scenarioStats.fork_daily}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(v: string) => v.slice(5)}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          `${value} 回`,
                          name,
                        ]}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                      {forkLanes.map((lane) => (
                        <Bar
                          key={lane.id}
                          dataKey={`counts.${lane.id}.executions`}
                          name={lane.label}
                          stackId="a"
                          fill={lane.color}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : !useForkBreakdown &&
                  scenarioStats?.daily &&
                  scenarioStats.daily.length > 0 &&
                  scenarioIds.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={scenarioStats.daily}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(v: string) => v.slice(5)}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || payload.length === 0)
                            return null
                          const sorted = [...payload].sort(
                            (a, b) => (b.value as number) - (a.value as number)
                          )
                          const total = sorted.reduce(
                            (sum, entry) => sum + (entry.value as number),
                            0
                          )
                          return (
                            <div className="bg-white border border-gray-200 rounded shadow-lg p-3 text-sm">
                              <p className="font-medium text-gray-900 mb-1">
                                {label}
                              </p>
                              {sorted.map((entry) => (
                                <div
                                  key={entry.dataKey as string}
                                  className="flex items-center gap-2"
                                >
                                  <span
                                    className="inline-block w-3 h-3 rounded-sm"
                                    style={{ backgroundColor: entry.color }}
                                  />
                                  <span className="text-gray-700">
                                    {entry.name}:
                                  </span>
                                  <span className="font-medium">
                                    {entry.value} 回
                                  </span>
                                </div>
                              ))}
                              <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-medium text-gray-900">
                                <span>合計:</span>
                                <span>{total} 回</span>
                              </div>
                            </div>
                          )
                        }}
                      />

                      {scenarioIds.map((id, i) => (
                        <Bar
                          key={id}
                          dataKey={`${id}.executions`}
                          name={scenarioLabelMap[id] || id}
                          stackId="a"
                          fill={SCENARIO_COLORS[i % SCENARIO_COLORS.length]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400">
                    データがありません
                  </div>
                )}
              </div>

              {/* 日別走行距離 */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  日別走行距離
                </h2>
                {stats?.data && stats.data.some((d) => d.distance_km > 0) ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={stats.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(v) => v.slice(5)}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${v}`}
                      />
                      <Tooltip
                        labelFormatter={(label) => `${label}`}
                        formatter={(value: number, name: string) => [
                          `${Math.round(value * 100) / 100} km`,
                          useForkBreakdown ? name : '走行距離',
                        ]}
                      />
                      {useForkBreakdown && (
                        <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
                      )}
                      {useForkBreakdown ? (
                        forkLanes.map((lane) => (
                          <Bar
                            key={lane.id}
                            dataKey={`distance_by_fork.${lane.id}`}
                            name={lane.label}
                            stackId="d"
                            fill={lane.color}
                          />
                        ))
                      ) : (
                        <Bar
                          dataKey="distance_km"
                          fill="#3b82f6"
                          radius={[2, 2, 0, 0]}
                        />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-gray-400">
                    データがありません
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 日別シナリオ実行時刻 (稼働時間) */}
          {!isLoading && (
            <div
              className={`bg-white rounded-lg shadow p-6 ${isAnyFetching ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center justify-between gap-4 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  日別シナリオ実行時刻
                </h2>
                {/* 軸の切替: 横=日付/縦=時刻 (既定) ⇔ 横=時刻/縦=日付 */}
                <div
                  className="inline-flex rounded-md border border-gray-300 overflow-hidden text-xs"
                  role="group"
                  aria-label="軸の切替"
                >
                  {(
                    [
                      ['date-x', '横: 日付 / 縦: 時刻'],
                      ['time-x', '横: 時刻 / 縦: 日付'],
                    ] as const
                  ).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setTimelineOrientation(value)}
                      className={`px-3 py-1.5 transition-colors ${
                        timelineOrientation === value
                          ? 'bg-primary-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              {scenarioStats?.daily.some((d) => d.runs && d.runs.length > 0) ? (
                <ScenarioRunTimeline
                  daily={scenarioStats.daily}
                  scenarios={scenarioStats.scenarios}
                  scenarioLabelMap={scenarioLabelMap}
                  scenarioColorMap={scenarioColorMap}
                  lanes={useForkBreakdown ? forkLanes : undefined}
                  height={300}
                  orientation={timelineOrientation}
                />
              ) : (
                <div className="flex items-center justify-center h-[300px] text-gray-400">
                  データがありません
                </div>
              )}
            </div>
          )}

          {/* シナリオ別テーブル */}
          <div
            className={`bg-white rounded-lg shadow ${isAnyFetching ? 'opacity-60' : ''}`}
          >
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                シナリオ別集計
              </h2>
            </div>
            {isLoading ? (
              <div className="flex flex-col items-center justify-center h-48 gap-2">
                <LoadingSpinner size="lg" />
                <span className="text-gray-600">シナリオデータを取得中...</span>
              </div>
            ) : scenarioStats && scenarioStats.scenarios.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="w-8 px-3 py-3"></th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        シナリオ
                      </th>
                      {isMultiFork && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          機体
                        </th>
                      )}
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        実行回数
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        完了
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        中断
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        エラー停止
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        完了率
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {scenarioStats.scenarios.map((scenario) => {
                      const completionRate =
                        scenario.executions > 0
                          ? Math.round(
                              (scenario.completions / scenario.executions) * 100
                            )
                          : 0
                      const isExpanded = expandedScenarioId === scenario.id
                      const hasTrajectories =
                        scenario.trajectories &&
                        scenario.trajectories.length > 0
                      return (
                        <>
                          <tr
                            key={scenario.id}
                            className={`hover:bg-gray-50 ${hasTrajectories ? 'cursor-pointer' : ''}`}
                            onClick={() =>
                              hasTrajectories && toggleExpand(scenario.id)
                            }
                          >
                            <td className="px-3 py-4 text-gray-400">
                              {hasTrajectories &&
                                (isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                ))}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {getScenarioLabel(scenario)}
                            </td>
                            {isMultiFork && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {scenario.fork_ids?.join(', ') ?? ''}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 text-right">
                              {scenario.executions}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 text-right">
                              {scenario.completions}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-red-500 text-right">
                              {scenario.cancellations}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-500 text-right">
                              {scenario.error_stops ?? '－'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <span
                                className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                  completionRate >= 80
                                    ? 'bg-green-100 text-green-800'
                                    : completionRate >= 50
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                }`}
                              >
                                {completionRate}%
                              </span>
                            </td>
                          </tr>
                          {isExpanded && scenario.trajectories && (
                            <tr key={`${scenario.id}-route`}>
                              <td
                                colSpan={isMultiFork ? 8 : 7}
                                className="px-6 py-4 bg-gray-50"
                              >
                                <div className="max-w-lg">
                                  <p className="text-sm font-medium text-gray-700 mb-2">
                                    ルートプレビュー（最新の実行）
                                  </p>
                                  <PreviewPathCanvas
                                    trajectories={scenario.trajectories}
                                  />
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                指定期間のシナリオデータがありません
              </div>
            )}
          </div>

          {/* エラー別集計（機体イベントジャーナル対応機体のみ） */}
          {!isLoading &&
            scenarioStats?.error_stats &&
            scenarioStats.error_stats.breakdown.length > 0 && (
              <div
                className={`bg-white rounded-lg shadow ${isAnyFetching ? 'opacity-60' : ''}`}
              >
                <div className="px-6 py-4 border-b border-gray-200 flex items-baseline justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    エラー別集計
                  </h2>
                  <p className="text-xs text-gray-400">
                    対応バージョンのソフトが入っている機体のみ集計
                    {(scenarioStats.total_pause_count ?? 0) > 0 && (
                      <>
                        {' '}
                        / 一時停止 {scenarioStats.total_pause_count} 回・
                        {formatDuration(scenarioStats.total_pause_ms ?? 0)}
                      </>
                    )}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          コード
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          内容
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          発生回数
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          実行停止
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          停止時間合計
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {scenarioStats.error_stats.breakdown.map((e) => (
                        <tr key={e.code} className="hover:bg-gray-50">
                          <td className="px-6 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                (e.level ?? 0) >= 2
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {e.code}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-gray-700 max-w-md truncate">
                            {e.message || '－'}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                            {e.occurred}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-orange-500 text-right">
                            {e.stops}
                          </td>
                          <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                            {formatDuration(e.downtime_ms)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
        </>
      )}

      {/* PDF レポート用の画面外レイアウト (生成中のみマウント) */}
      {isExportingPdf && searchParams && (
        <div
          aria-hidden
          className="absolute top-0 left-[-10000px] pointer-events-none"
        >
          <MetricsReport
            ref={reportRef}
            forkId={forkLabel}
            siteId={reportSiteId}
            period={{
              start: searchParams.start_date,
              end: searchParams.end_date,
            }}
            stats={stats}
            scenarioStats={scenarioStats}
            onRendered={handleReportRendered}
            timelineOrientation={timelineOrientation}
          />
        </div>
      )}
    </div>
  )
}
